'use client';

import React, { useEffect } from 'react';
import * as styles from './Facets.styles';
import Accordion from '../accordion/accordion';
import {
  DistinctFacetFragment,
  RangeFacetFragment,
} from '@business/gql/graphql';
import { DistinctFacets, FacetRange } from './FacetSearch';
import { Maybe } from 'graphql/jsutils/Maybe';
import { Dictionary } from '@ts/dictionary';
import { usePathname, useSearchParams } from 'next/navigation';
import useShallowSearchParams from '@utils/hooks/sort/useShallowSearchParams';
import getNumberOfActiveFilters from '@utils/hooks/sort/helpers/getNumberOfActiveFilters';

type Sizes = 10;

const getOpenAccordion = ({
  isMobile,
  facetName,
}: {
  isMobile: boolean;
  facetName: string;
}): boolean => {
  if (isMobile) {
    if (facetName === 'Designer') return false;
    if (facetName === 'Brand') return false;
  }
  return true;
};

type Props = {
  visibleFacets?: Sizes;
  facets: Maybe<DistinctFacetFragment | RangeFacetFragment>[] | undefined;
  isMobile?: boolean;
  dictionary: Dictionary;
};

export default function FacetContentSearch({
  visibleFacets = 10,
  facets,
  isMobile = false,
  dictionary,
}: Props) {
  const searchParams = useSearchParams();
  const {
    resetShallowSearchParams,
    shallowSearchParams,
    setShallowSearchParams,
  } = useShallowSearchParams();
  const pathname = usePathname();

  useEffect(() => {
    if (
      searchParams?.has('category') ||
      searchParams?.has('featured') ||
      searchParams?.has('brand') ||
      searchParams?.has('designer') ||
      searchParams?.has('sort') ||
      searchParams?.has('price')
    ) {
      const searchParamsAsString = searchParams.toString();
      const href = `${pathname}?${searchParamsAsString}`;
      setShallowSearchParams(href, searchParamsAsString);
      return; // without this we would reset state which is derived from searchParams on initial load and on back/forward navigation
    }
    resetShallowSearchParams();
  }, [
    pathname,
    resetShallowSearchParams,
    searchParams,
    setShallowSearchParams,
  ]);
  return (
    <>
      {facets?.map((facet) => {
        if (!facet) return null;
        if (
          facet.__typename === 'DistinctFacet' &&
          facet.items?.length &&
          facet.items?.some((f) => f?.count)
        ) {
          const numberOfActiveFilters =
            getNumberOfActiveFilters(
              shallowSearchParams,
              facet.name?.toLocaleLowerCase(),
            ) || undefined;
          const openAccordion = getOpenAccordion({
            isMobile,
            facetName: facet.name,
          });

          return (
            <Accordion
              key={`${facet.name}-${facet?.items
                ?.map((v) => v?.item)
                .join('-')}`}
              space={visibleFacets ? 'normal' : 'overflowing'}
              name={dictionary?.[facet.name?.toLowerCase()]}
              count={numberOfActiveFilters}
              open={openAccordion}>
              <div className={styles.container({ size: visibleFacets })}>
                {facet && (
                  <DistinctFacets dictionary={dictionary} facet={facet} />
                )}
              </div>
            </Accordion>
          );
        }
        if (facet?.__typename === 'RangeFacet') {
          const numberOfActiveFilters =
            getNumberOfActiveFilters(
              shallowSearchParams,
              facet?.name?.toLocaleLowerCase(),
            ) || undefined;
          return (
            <Accordion
              key={`${facet.name}-${facet.__typename}`}
              space={'normal'}
              count={numberOfActiveFilters}
              name={dictionary?.[facet.name?.toLowerCase()]}
              open>
              <div className={styles.container({ size: visibleFacets })}>
                {facet && (
                  <FacetRange
                    minLabel={dictionary.minPrice}
                    maxLabel={dictionary.maxPrice}
                    facet={facet}
                  />
                )}
              </div>
            </Accordion>
          );
        }
      })}
    </>
  );
}
